import React from "react";
import { ComboBoxItem } from "@/utils/helper.util";

import Badge from "@/components/atoms/Badge";
import Chip from "@/components/atoms/Chip";

type SelectedOptionsProps<T> = {
  options?: T[];
  value: T[];
  displayValue: (option: T) => string | React.ReactNode;
  renderChip?: (item: T, handleRemove: (item: T) => void) => React.ReactNode;
  chipClassName?: string;
  handleRemoveSelectedItem: (item: T) => void;
  maxDisplayedChips?: number;
  onSeeAllValues?: () => void;
};

// TODO Instead of checking if displayedItems also contains the item's name, we should just use the options array to ALWAYS find the name.
const SelectedOptions = <T extends ComboBoxItem>({
  value,
  displayValue,
  renderChip,
  handleRemoveSelectedItem,
  options,
  chipClassName = "max-w-28",
  maxDisplayedChips,
  onSeeAllValues,
}: SelectedOptionsProps<T>) => {
  if (!Array.isArray(value) || value.length === 0) return null;

  const shouldTruncate = !!maxDisplayedChips && value.length > maxDisplayedChips;

  const displayedItems = shouldTruncate ? value.slice(0, maxDisplayedChips) : value;

  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid={"selected-options"}>
      {displayedItems.map((item) =>
        renderChip ? (
          renderChip(
            item.name === undefined ? (options?.find((option) => option.id === item.id) ?? item) : item,
            handleRemoveSelectedItem,
          )
        ) : (
          <Chip
            data-testid={`selected-option-${item.id}`}
            size={"xs"}
            color={"natural"}
            key={item.id}
            label={displayValue(item) ?? options?.find((option) => option.id === item.id)?.name}
            className={chipClassName}
            onRemove={() => handleRemoveSelectedItem(item)}
          />
        ),
      )}

      {shouldTruncate && (
        <Badge
          onClick={onSeeAllValues}
          size={"sm"}
          color={"natural"}
          label={`+${value.length - displayedItems.length} more`}
        />
      )}
    </div>
  );
};

export default SelectedOptions;
